// ─── Export du calendrier (feature-calendar) ──────────────────────
// Repris de MoPCal (`services/export.js`), réduit à ce que la suite sait
// produire aujourd'hui. L'ICS et le rapport sont 100 % JavaScript ; seul le PDF
// mobile passe par expo-print/expo-sharing, chargés en require PROTÉGÉ — absents,
// on retombe sur le partage du document au lieu de planter.
//
//   genererICS()   → fichier .ics (iCalendar) importable dans Google Agenda,
//                    Apple Calendrier, Outlook. UID stable → réimporter met à
//                    jour au lieu de dupliquer.
//   genererHTML()  → document imprimable, source du PDF.
//   exporterPDF()  → PDF enregistrable (mobile) / impression navigateur (web).
//   partager()     → web : téléchargement ; mobile : partage natif.
import { Platform, Share } from 'react-native';

const pad = (n) => String(n).padStart(2, '0');

// Heure au format français ('16h37', '8h05') ou ISO ('16:37') → { h, m }.
function parseHeure(heure) {
  const m = String(heure || '').match(/(\d{1,2})\s*[h:]\s*(\d{0,2})/i);
  return m ? { h: parseInt(m[1], 10), m: parseInt(m[2] || '0', 10) || 0 } : null;
}

// 'YYYY-MM-DD' (+ heure) → 'YYYYMMDD' ou 'YYYYMMDDTHHMMSS' (heure locale).
function icsDateTime(dateKey, heure) {
  const d = String(dateKey || '').replace(/-/g, '');
  if (!heure) return d;
  const p = parseHeure(heure);
  return p ? `${d}T${pad(p.h)}${pad(p.m)}00` : d;
}

const icsEscape = (s) => String(s || '')
  .replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const _d = (dk) => { const [y, m, j] = String(dk).split('-').map(Number); return new Date(y, m - 1, j); };
const _dk = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const _addJ = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
const fmtFr = (dk) => { const [y, m, j] = String(dk).split('-'); return `${j}/${m}/${y}`; };

// DTSTAMP UTC : sans lui, Google Agenda et Outlook REFUSENT le fichier.
function icsStamp() {
  const d = new Date();
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`
       + `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

// evenements : [{ date:'YYYY-MM-DD', dateFin?, heure?, heureFin?, titre, lieu?, note?, id? }]
const trier = (l) => [...(l || [])].sort((a, b) =>
  (a.date || '').localeCompare(b.date || '') || (a.heure || '').localeCompare(b.heure || ''));

const plusUneHeure = (heure) => {
  const p = parseHeure(heure);
  return p ? `${pad((p.h + 1) % 24)}:${pad(p.m)}` : heure;
};

export function genererICS({ nomCalendrier, calId, evenements, gardes }) {
  const stamp = icsStamp();
  // Référence du calendrier dans l'UID : deux calendriers exportés sur les
  // mêmes dates ne se télescopent pas dans un même agenda.
  const ref = String(calId || nomCalendrier || 'cal').replace(/[^a-zA-Z0-9]/g, '').slice(0, 12) || 'cal';
  const L = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//MoP Suite//My Calendar//FR',
    'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    `X-WR-CALNAME:${icsEscape(nomCalendrier || 'My Calendar')}`,
  ];

  // Segments de garde → journées entières.
  (gardes || []).forEach((g, i) => {
    L.push('BEGIN:VEVENT');
    L.push(`UID:mop-${ref}-garde-${g.debut}-${i}@mopsuite`);
    L.push(`DTSTAMP:${stamp}`);
    L.push(`DTSTART;VALUE=DATE:${icsDateTime(g.debut)}`);
    L.push(`DTEND;VALUE=DATE:${icsDateTime(_dk(_addJ(_d(g.fin || g.debut), 1)))}`);  // DTEND exclusif
    L.push(`SUMMARY:${icsEscape(`Garde : ${g.nom || ''}`)}`);
    L.push('END:VEVENT');
  });

  trier(evenements).forEach((e, i) => {
    L.push('BEGIN:VEVENT');
    L.push(`UID:mop-${ref}-${e.id || `${e.date}-${i}`}@mopsuite`);
    L.push(`DTSTAMP:${stamp}`);
    if (e.dateFin && e.dateFin > e.date) {
      if (e.heure) {
        L.push(`DTSTART:${icsDateTime(e.date, e.heure)}`);
        L.push(`DTEND:${icsDateTime(e.dateFin, e.heureFin || e.heure)}`);
      } else {
        L.push(`DTSTART;VALUE=DATE:${icsDateTime(e.date)}`);
        L.push(`DTEND;VALUE=DATE:${icsDateTime(_dk(_addJ(_d(e.dateFin), 1)))}`);
      }
    } else if (e.heure) {
      // Sans heure de fin, +1 h : une durée nulle s'affiche mal dans les agendas.
      L.push(`DTSTART:${icsDateTime(e.date, e.heure)}`);
      L.push(`DTEND:${icsDateTime(e.date, e.heureFin || plusUneHeure(e.heure))}`);
    } else {
      L.push(`DTSTART;VALUE=DATE:${icsDateTime(e.date)}`);
      L.push(`DTEND;VALUE=DATE:${icsDateTime(_dk(_addJ(_d(e.date), 1)))}`);
    }
    L.push(`SUMMARY:${icsEscape(e.titre || 'Événement')}`);
    if (e.lieu) L.push(`LOCATION:${icsEscape(e.lieu)}`);
    if (e.note) L.push(`DESCRIPTION:${icsEscape(e.note)}`);
    L.push('END:VEVENT');
  });

  L.push('END:VCALENDAR');
  return L.join('\r\n');   // la norme iCalendar impose CRLF
}

const JLABEL = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const COUL_P1 = '#FF6B8A', COUL_P2 = '#4A90D9', COUL_NEUTRE = '#9E86F0';

// Lundi de la semaine contenant `dk` (semaine ISO : lundi → dimanche).
function lundiDe(dk) {
  const d = _d(dk);
  const j = (d.getDay() + 6) % 7;          // 0 = lundi
  return _addJ(d, -j);
}
// Numéro de semaine ISO — repris du Core (`numeroSemaineISO`) pour ne pas
// dépendre de l'app dans un module de génération.
function numSemaine(d) {
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  t.setDate(t.getDate() + 3 - ((t.getDay() + 6) % 7));
  const premier = new Date(t.getFullYear(), 0, 4);
  return 1 + Math.round(((t - premier) / 86400000 - 3 + ((premier.getDay() + 6) % 7)) / 7);
}

// Planning SEMAINE PAR SEMAINE : c'est la forme qui rend un rapport de garde
// lisible — une grille mensuelle n'a pas la place d'afficher les événements.
// Une semaine SANS garde, sans vacances et sans événement est sautée : un PDF
// de pages vides ne sert personne.
export function genererGrillesHTML({ debut, fin, gardeParJour, parents, evenements, vacances, edtParJour, plage }) {
  const nomP = (g) => (g === 'parent1' ? (parents?.parent1?.prenom || 'Parent 1')
                     : g === 'parent2' ? (parents?.parent2?.prenom || 'Parent 2') : '');
  const coulP = (g) => (g === 'parent1' ? COUL_P1 : g === 'parent2' ? COUL_P2 : COUL_NEUTRE);
  const vacDe = (dk) => (vacances || []).find(v => v.debut && v.fin && v.debut <= dk && dk <= v.fin) || null;

  const parJour = {};
  trier(evenements).forEach(e => { (parJour[e.date] = parJour[e.date] || []).push(e); });
  // Les COURS rejoignent les événements : c'est la journée réelle qu'on
  // imprime. Ils gardent leur puce (🎓) pour rester identifiables.
  Object.entries(edtParJour || {}).forEach(([dk, items]) => {
    (items || []).forEach(c => { (parJour[dk] = parJour[dk] || []).push({ ...c, date: dk, _edt: true }); });
  });

  // ── Échelle horaire ──────────────────────────────────────────────
  // Toutes les lignes de la plage, pas seulement les heures où quelque chose
  // commence : sans ça un cours de 3 h occupe une seule ligne et les créneaux
  // qu'il recouvre paraissent libres.
  const enMin = (h) => { const p = parseHeure(h); return p ? p.h * 60 + p.m : null; };
  const hhmm = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
  let bas = enMin(plage?.debut) ?? null, haut = enMin(plage?.fin) ?? null;
  const pas = Number(plage?.pas) > 0 ? Number(plage.pas) : 30;
  Object.values(parJour).forEach(l => l.forEach(e => {
    const d = enMin(e.heure); if (d == null) return;
    const f = enMin(e.heureFin) ?? d + pas;
    bas = bas == null ? d : Math.min(bas, d);
    haut = haut == null ? f : Math.max(haut, f);
  }));
  if (bas != null && haut != null && haut <= bas) haut = bas + pas;
  // Bornes calées sur le pas, pour que les libellés tombent juste.
  if (bas != null) bas = Math.floor(bas / pas) * pas;
  if (haut != null) haut = Math.ceil(haut / pas) * pas;
  const creneaux = [];
  if (bas != null && haut != null) for (let m = bas; m < haut; m += pas) creneaux.push(m);

  let html = '', auMoinsUne = false;
  let lundi = lundiDe(debut);
  const finD = _d(fin);

  while (lundi <= finD) {
    const jours = Array.from({ length: 7 }, (_, i) => _addJ(lundi, i));
    const dks = jours.map(_dk);
    const aGarde = dks.some(dk => (gardeParJour || {})[dk]);
    const aVac = dks.some(dk => vacDe(dk));
    const aQuelqueChose = dks.some(dk => (parJour[dk] || []).length);
    if (!aGarde && !aVac && !aQuelqueChose) { lundi = _addJ(lundi, 7); continue; }
    auMoinsUne = true;

    const enTete = jours.map((dd, i) => {
      const g = (gardeParJour || {})[dks[i]];
      const vac = vacDe(dks[i]);
      return `<th style="width:13%;background:${coulP(g)};color:#fff;border:1px solid #fff;padding:4px 3px;font-size:11px;word-break:break-word">`
        + `${JLABEL[i]} ${pad(dd.getDate())}/${pad(dd.getMonth() + 1)}`
        + (g ? `<br><span style="font-weight:400;font-size:10px">${esc(nomP(g))}</span>` : '')
        + (vac ? `<br><span style="font-weight:400;font-size:9px">\u{1F3D6} ${esc(vac.nom || 'Vacances')}</span>` : '')
        + '</th>';
    }).join('');

    // Bloc affiché DANS une case : titre, puis rappel début → fin. Le rappel
    // des deux bornes évite d'avoir à compter les lignes pour lire la durée.
    const bloc = (e) => `<div style="font-size:10px;line-height:1.25">`
      + `${e._edt ? '\u{1F393} ' : '\u2022 '}${esc(e.titre || '')}`
      + (e.heure ? `<br><span style="color:#666">${esc(hhmmDe(e.heure))}` + (e.heureFin ? ` \u2192 ${esc(hhmmDe(e.heureFin))}` : '') + '</span>' : '')
      + '</div>';

    let lignes = '';
    // Journée entière : une ligne à part, au-dessus de l'échelle.
    const aJournee = dks.some(dk => (parJour[dk] || []).some(e => !e.heure));
    if (aJournee) {
      lignes += '<tr><td style="background:#F3F4F6;font-size:10px;text-align:center;padding:3px">Journ\u00e9e</td>'
        + dks.map(dk => `<td style="border:1px solid #eee;padding:3px;vertical-align:top">`
          + (parJour[dk] || []).filter(e => !e.heure).map(bloc).join('') + '</td>').join('') + '</tr>';
    }

    // Occupation : par jour, quel créneau est pris et par quoi (avec sa hauteur).
    const occupe = {};
    dks.forEach(dk => {
      occupe[dk] = {};
      (parJour[dk] || []).filter(e => e.heure).forEach(e => {
        const d = enMin(e.heure); if (d == null) return;
        const f = Math.max((enMin(e.heureFin) ?? d + pas), d + pas);
        const i0 = creneaux.findIndex(m => m + pas > d);
        if (i0 < 0) return;
        let i1 = creneaux.findIndex(m => m >= f);
        if (i1 < 0) i1 = creneaux.length;
        occupe[dk][i0] = { e, span: Math.max(1, i1 - i0) };
        for (let k = i0 + 1; k < i1; k++) occupe[dk][k] = 'suite';
      });
    });

    creneaux.forEach((m, i) => {
      // L'heure est posée SUR la ligne (alignée en haut), pas centrée entre
      // deux lignes : sinon on ne sait pas à quelle séparation elle se rapporte.
      lignes += `<tr><td style="background:#F3F4F6;font-size:10px;text-align:right;padding:0 4px;`
        + `vertical-align:top;white-space:nowrap;color:#555;border-top:1px solid #ddd">${hhmm(m)}</td>`;
      dks.forEach(dk => {
        const o = occupe[dk][i];
        if (o === 'suite') return;                        // couvert par un rowspan
        if (!o) { lignes += '<td style="border:1px solid #eee;height:14px"></td>'; return; }
        lignes += `<td rowspan="${o.span}" style="border:1px solid #ccc;background:#F8F7FF;padding:2px 3px;vertical-align:top">${bloc(o.e)}</td>`;
      });
      lignes += '</tr>';
    });
    // Dernière ligne : borne HAUTE de la plage, pour lire la fin de journée.
    if (creneaux.length) {
      lignes += `<tr><td style="background:#F3F4F6;font-size:10px;text-align:right;padding:0 4px;`
        + `color:#555;border-top:1px solid #ddd">${hhmm(haut)}</td>`
        + dks.map(() => '<td style="border-top:1px solid #ddd"></td>').join('') + '</tr>';
    }
    if (!lignes) lignes = '<tr><td colspan="8" style="color:#aaa;font-size:11px;padding:6px;text-align:center">Rien cette semaine</td></tr>';

    html += `<table style="margin-top:14px;table-layout:fixed;width:100%;border-collapse:collapse;break-inside:avoid">`
      + `<thead><tr><th style="width:9%;background:#6C4FE6;color:#fff;padding:4px 3px;font-size:11px">S.${numSemaine(lundi)}`
      + `<br><span style="font-weight:400;font-size:9px">du ${pad(lundi.getDate())}/${pad(lundi.getMonth() + 1)}</span></th>${enTete}</tr></thead>`
      + `<tbody>${lignes}</tbody></table>`;
    lundi = _addJ(lundi, 7);
  }
  return auMoinsUne ? `<h2>Planning \u2014 semaine par semaine</h2>${html}` : '';
}

// '8h05' | '08:05' → '08:05' (affichage homogène dans tout le document).
function hhmmDe(h) {
  const p = parseHeure(h);
  return p ? `${pad(p.h)}:${pad(p.m)}` : String(h || '');
}

// Rapport imprimable : un bloc par jour, dans l'ordre.
export function genererHTML({ nomCalendrier, periodeLabel, evenements, gardes, grilles, paysage }) {
  const parJour = {};
  trier(evenements).forEach(e => { (parJour[e.date] = parJour[e.date] || []).push(e); });
  const gardeDe = (dk) => (gardes || []).find(g => dk >= g.debut && dk <= (g.fin || g.debut));

  const jours = Object.keys(parJour).sort().map(dk => `
    <section class="j">
      <h2>${esc(fmtFr(dk))}${gardeDe(dk) ? ` <span class="g">· ${esc(gardeDe(dk).nom || '')}</span>` : ''}</h2>
      <ul>${parJour[dk].map(e => `<li>
        <b>${esc(e.heure || '')}</b> ${esc(e.titre || '')}
        ${e.lieu ? `<span class="m">· ${esc(e.lieu)}</span>` : ''}
        ${e.note ? `<div class="n">${esc(e.note)}</div>` : ''}
      </li>`).join('')}</ul>
    </section>`).join('');

  return `<html><head><meta charset="utf-8"><title>${esc(nomCalendrier || 'Calendrier')}</title>
<style>
 /* Orientation demandee : @page pilote l'impression navigateur ET le moteur
    d'impression systeme (expo-print) ; les dimensions sont aussi passees a
    printToFileAsync, les deux moteurs n'honorant pas toujours la meme chose. */
 @page { size: A4 ${paysage ? 'landscape' : 'portrait'}; margin: 10mm; }
 body{font-family:-apple-system,Helvetica,Arial,sans-serif;padding:24px;color:#1a1a1a}
 h1{font-size:20px;margin:0 0 2px} .meta{color:#666;font-size:12px;margin-bottom:18px}
 .j{break-inside:avoid;margin-bottom:14px}
 h2{font-size:14px;margin:0 0 4px;border-bottom:1px solid #ddd;padding-bottom:3px;color:#4b3fd6}
 .g{color:#666;font-weight:400}
 ul{margin:0;padding-left:18px} li{font-size:13px;margin:3px 0}
 .m{color:#666} .n{color:#666;font-size:12px;font-style:italic}
</style></head><body>
<h1>${esc(nomCalendrier || 'Calendrier')}</h1>
<div class="meta">${esc(periodeLabel || '')} · ${trier(evenements).length} événement(s)${(gardes || []).length ? ` · ${gardes.length} période(s) de garde` : ''}</div>
${(gardes || []).length ? `<section class="j"><h2>Gardes</h2><ul>${gardes.map(g =>
  `<li><b>${esc(fmtFr(g.debut))}${g.fin && g.fin !== g.debut ? ` → ${esc(fmtFr(g.fin))}` : ''}</b> · ${esc(g.nom || '')}</li>`).join('')}</ul></section>` : ''}
${grilles || ''}
${jours || '<p>Aucun événement sur la période.</p>'}
</body></html>`;
}

// Web : téléchargement du fichier. Mobile : partage natif du contenu.
export async function partager({ nomFichier, contenu, mimeType, titre }) {
  if (Platform.OS === 'web') {
    const blob = new Blob([contenu], { type: mimeType || 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = nomFichier;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return { ok: true, methode: 'download' };
  }
  await Share.share({ title: titre || 'My Calendar', message: contenu });
  return { ok: true, methode: 'share' };
}

// PDF réellement enregistrable (pas seulement « imprimable ») :
//   • mobile  → expo-print produit un fichier, expo-sharing ouvre la feuille de
//     partage — d'où « Enregistrer dans Fichiers » ;
//   • web     → il n'existe pas d'API d'écriture de PDF : on ouvre la boîte
//     d'impression du navigateur, dont la destination « Enregistrer au format
//     PDF » fait le même travail.
// Les deux modules sont chargés en require PROTÉGÉ : absents (build sans eux),
// on retombe sur l'impression au lieu de planter.
export async function exporterPDF({ html, titre, paysage }) {
  if (Platform.OS === 'web') {
    const w = window.open('', '_blank');
    if (!w) return { ok: false, motif: 'popup' };
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch {} }, 400);
    return { ok: true, methode: 'impression' };
  }
  try {
    const Print = require('expo-print');
    // Dimensions en POINTS (A4 = 595 x 842). En paysage on les inverse : la
    // regle @page seule n'est pas toujours honoree par le moteur natif.
    const { uri } = await Print.printToFileAsync(
      paysage ? { html, width: 842, height: 595 } : { html });
    try {
      const Sharing = require('expo-sharing');
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, {
          mimeType: 'application/pdf', UTI: 'com.adobe.pdf', dialogTitle: titre || 'Calendrier',
        });
        return { ok: true, methode: 'pdf' };
      }
    } catch { /* partage indisponible : le fichier existe quand même */ }
    return { ok: true, methode: 'fichier', uri };
  } catch (e) {
    return { ok: false, motif: 'module', erreur: e };
  }
}

export const nomFichierSafe = (nom, ext) =>
  `${String(nom || 'calendrier').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'calendrier'}.${ext}`;
